import { latLngToLocalMeters } from '@roadworld/map-generator';
import type { ChunkPayload, OsmWay } from '@roadworld/shared';
import type { HeightfieldData } from './geometry.js';
import { sampleHeightfield } from './heightfield.js';

/**
 * Per-sample data along a road's smoothed centerline. All fields are pre-computed once so
 * downstream builders (ribbon, markings, shoulder, curb, bollards) all consume the same
 * underlying geometry and stay perfectly aligned.
 */
export interface CenterlineSample {
  /** Local meters (chunk frame). */
  x: number;
  z: number;
  /** Surface Y, includes the requested `lift`. For bridges, follows the deck ramp. */
  h: number;
  /** Unit tangent in XZ. */
  dirX: number;
  dirZ: number;
  /** Unit perpendicular in XZ (90° CCW from tangent), pointing to the "left" side. */
  perpX: number;
  perpZ: number;
  /** Cumulative arc length from the start of the centerline. */
  distance: number;
  /** Per-vertex width multiplier (1 = baseWidth). Only non-1 for path-bridge tapers. */
  widthBoost: number;
}

/**
 * Build a smoothed, terrain-sampled centerline for an OSM way.
 *
 * Smoothing uses centripetal Catmull-Rom (alpha=0.5) between the raw OSM node positions,
 * then samples every ~`sampleSpacing` meters along each curved segment. Each sample picks
 * up the terrain height plus `lift`. Returns null when the way has fewer than 2 nodes.
 *
 * Endpoints are anchored exactly on the first and last OSM nodes so adjacent ways meeting
 * at a shared node still join cleanly.
 */
export function buildSmoothedCenterline(
  payload: ChunkPayload,
  way: OsmWay,
  heightfield: HeightfieldData,
  lift: number,
  sampleSpacing: number,
): CenterlineSample[] | null {
  // Project OSM nodes into local meters.
  const osm: Array<{ x: number; z: number }> = [];
  for (const nodeId of way.nodes) {
    const node = payload.osm.nodes[nodeId];
    if (!node) continue;
    const { x, z } = latLngToLocalMeters(node.lat, node.lng, payload.origin.lat, payload.origin.lng);
    osm.push({ x, z });
  }
  if (osm.length < 2) return null;

  // Catmull-Rom needs a phantom point before [0] and after [n-1]. Mirror the first/last
  // edge outward so the curve starts/ends tangent to the original polyline (no overshoot).
  const phantomStart = {
    x: osm[0]!.x * 2 - osm[1]!.x,
    z: osm[0]!.z * 2 - osm[1]!.z,
  };
  const phantomEnd = {
    x: osm[osm.length - 1]!.x * 2 - osm[osm.length - 2]!.x,
    z: osm[osm.length - 1]!.z * 2 - osm[osm.length - 2]!.z,
  };

  const xy: number[] = [];
  // First point.
  xy.push(osm[0]!.x, osm[0]!.z);

  for (let i = 0; i < osm.length - 1; i++) {
    const p0 = i === 0 ? phantomStart : osm[i - 1]!;
    const p1 = osm[i]!;
    const p2 = osm[i + 1]!;
    const p3 = i + 2 < osm.length ? osm[i + 2]! : phantomEnd;

    const segLen = Math.hypot(p2.x - p1.x, p2.z - p1.z);
    const subdivisions = Math.max(1, Math.ceil(segLen / sampleSpacing));

    for (let k = 1; k <= subdivisions; k++) {
      const t = k / subdivisions;
      const [x, z] = catmullRomCentripetal(p0, p1, p2, p3, t);
      xy.push(x, z);
    }
  }

  // Build per-sample data (height + tangent/perp + cumulative arc length).
  const sampleCount = xy.length / 2;
  if (sampleCount < 2) return null;

  const out: CenterlineSample[] = new Array(sampleCount);
  let distance = 0;
  for (let i = 0; i < sampleCount; i++) {
    const x = xy[i * 2]!;
    const z = xy[i * 2 + 1]!;
    const prevX = xy[Math.max(0, i - 1) * 2]!;
    const prevZ = xy[Math.max(0, i - 1) * 2 + 1]!;
    const nextX = xy[Math.min(sampleCount - 1, i + 1) * 2]!;
    const nextZ = xy[Math.min(sampleCount - 1, i + 1) * 2 + 1]!;

    let dirX = nextX - prevX;
    let dirZ = nextZ - prevZ;
    const dirLen = Math.hypot(dirX, dirZ) || 1;
    dirX /= dirLen;
    dirZ /= dirLen;

    if (i > 0) {
      distance += Math.hypot(x - prevX, z - prevZ);
    }

    out[i] = {
      x,
      z,
      h: sampleHeightfield(heightfield, x, z) + lift,
      dirX,
      dirZ,
      // 90° CCW in XZ (looking down -Y): perpendicular = (-dirZ, dirX)
      perpX: -dirZ,
      perpZ: dirX,
      distance,
      widthBoost: 1,
    };
  }

  return out;
}

/**
 * Interpolate a full CenterlineSample at an arbitrary arc-length distance. Clamps to the
 * endpoints if `distance` is outside [0, totalLength]. The returned sample's `distance`
 * equals the clamped arc length, which lets callers use it interchangeably with elements
 * from the source centerline (e.g. when building a trimmed sub-centerline).
 */
export function sampleCenterlineAt(centerline: CenterlineSample[], distance: number): CenterlineSample {
  if (centerline.length === 0) {
    return { x: 0, z: 0, h: 0, dirX: 1, dirZ: 0, perpX: 0, perpZ: 1, distance: 0, widthBoost: 1 };
  }
  if (distance <= 0) return { ...centerline[0]!, distance: 0 };
  const last = centerline[centerline.length - 1]!;
  if (distance >= last.distance) return { ...last };

  // Binary search for the bracketing pair.
  let lo = 0;
  let hi = centerline.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (centerline[mid]!.distance <= distance) lo = mid;
    else hi = mid;
  }
  const a = centerline[lo]!;
  const b = centerline[hi]!;
  const span = b.distance - a.distance;
  const t = span > 0 ? (distance - a.distance) / span : 0;
  return {
    x: a.x + (b.x - a.x) * t,
    z: a.z + (b.z - a.z) * t,
    h: a.h + (b.h - a.h) * t,
    dirX: a.dirX + (b.dirX - a.dirX) * t,
    dirZ: a.dirZ + (b.dirZ - a.dirZ) * t,
    perpX: a.perpX + (b.perpX - a.perpX) * t,
    perpZ: a.perpZ + (b.perpZ - a.perpZ) * t,
    distance,
    widthBoost: a.widthBoost + (b.widthBoost - a.widthBoost) * t,
  };
}

/**
 * Return a contiguous sub-centerline covering arc length [startDist, endDist], with the
 * start and end interpolated exactly so the resulting strip has clean caps. Drops samples
 * within a 5cm epsilon of either bound to avoid degenerate quads.
 *
 * Returns an empty array if the requested range is too short to draw.
 */
export function trimmedCenterline(
  centerline: CenterlineSample[],
  startDist: number,
  endDist: number,
): CenterlineSample[] {
  if (centerline.length < 2) return [];
  const total = centerline[centerline.length - 1]!.distance;
  const lo = Math.max(0, Math.min(startDist, total));
  const hi = Math.max(lo, Math.min(endDist, total));
  if (hi - lo < 0.5) return [];
  const out: CenterlineSample[] = [];
  out.push(sampleCenterlineAt(centerline, lo));
  for (const s of centerline) {
    if (s.distance > lo + 0.05 && s.distance < hi - 0.05) out.push(s);
  }
  out.push(sampleCenterlineAt(centerline, hi));
  return out;
}

/**
 * Centripetal Catmull-Rom (alpha=0.5) interpolation. Centripetal parameterization avoids
 * the cusps and self-intersections that uniform CR produces on tight turns.
 *
 * Returns the interpolated (x, z) at t in [0, 1] between p1 and p2.
 */
function catmullRomCentripetal(
  p0: { x: number; z: number },
  p1: { x: number; z: number },
  p2: { x: number; z: number },
  p3: { x: number; z: number },
  t: number,
): [number, number] {
  const alpha = 0.5;
  const eps = 1e-6;

  const dt01 = Math.max(eps, Math.pow(Math.hypot(p1.x - p0.x, p1.z - p0.z), alpha));
  const dt12 = Math.max(eps, Math.pow(Math.hypot(p2.x - p1.x, p2.z - p1.z), alpha));
  const dt23 = Math.max(eps, Math.pow(Math.hypot(p3.x - p2.x, p3.z - p2.z), alpha));

  // Tangents at p1 and p2 (non-uniform CR formulation).
  const m1x = ((p1.x - p0.x) / dt01 - (p2.x - p0.x) / (dt01 + dt12) + (p2.x - p1.x) / dt12) * dt12;
  const m1z = ((p1.z - p0.z) / dt01 - (p2.z - p0.z) / (dt01 + dt12) + (p2.z - p1.z) / dt12) * dt12;
  const m2x = ((p2.x - p1.x) / dt12 - (p3.x - p1.x) / (dt12 + dt23) + (p3.x - p2.x) / dt23) * dt12;
  const m2z = ((p2.z - p1.z) / dt12 - (p3.z - p1.z) / (dt12 + dt23) + (p3.z - p2.z) / dt23) * dt12;

  // Hermite basis.
  const t2 = t * t;
  const t3 = t2 * t;
  const h00 = 2 * t3 - 3 * t2 + 1;
  const h10 = t3 - 2 * t2 + t;
  const h01 = -2 * t3 + 3 * t2;
  const h11 = t3 - t2;

  return [
    h00 * p1.x + h10 * m1x + h01 * p2.x + h11 * m2x,
    h00 * p1.z + h10 * m1z + h01 * p2.z + h11 * m2z,
  ];
}
